import { useState, useMemo, useEffect } from 'react';
import Field from '../Field';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { nextBirthday, countdownLabel, revealAfterBirthday } from '../../utils/dates';
import WishFormModal, { BLANK_WISH, wishToForm } from '../WishFormModal';
import WishGiftCard from '../WishGiftCard';
import { wishOwner, ownerKey } from '../../utils/wishOwner';
import Avatar from '../Avatar';
import PetBadge from '../PetBadge';
import PersonFilterBar from '../PersonFilterBar';

function errText(err, fallback) {
    return err.response?.data?.error || fallback;
}

/**
 * WishesTab — the wish list for one group. The create/edit form lives in the
 * shared WishFormModal (also used by the My Wishes page).
 *
 * Owner-blindness rule (defense in depth, on top of the API): for a wish the
 * logged-in user OWNS, we NEVER render claim/purchase state or gifting buttons.
 * That branch lives in the shared WishGiftCard, which derives it from each
 * wish's own `user_id` — this tab does not get its own copy to drift.
 */
export default function WishesTab({ group, wishes, events, user, myGroups, categories, pets = [], focusUserId, onClearFocus, onChanged, notify, childrenList = []}) {
    const navigate = useNavigate();
    const [modal, setModal] = useState(null); // { editingId, initialForm }

    // Purchase modal state.
    const [purchaseTarget, setPurchaseTarget] = useState(null); // wish being purchased
    const [purchaseNote, setPurchaseNote] = useState('');
    const [revealDate, setRevealDate] = useState('');

    // Group the wishes by their DISPLAY owner so the list reads as a per-owner
    // breakdown. A wish for a pet is sectioned under the pet (not the caretaker),
    // matching how it renders everywhere else. The current user's own list floats
    // to the top; pets and everyone else follow alphabetically.
    const ownerSections = useMemo(() => {
        const byOwner = new Map();
        for (const w of wishes) {
            const owner = wishOwner(w);
            const key = ownerKey(w);
            if (!byOwner.has(key)) {
                byOwner.set(key, { key, owner, userId: w.user_id, isPet: owner.isPet, wishes: [] });
            }
            byOwner.get(key).wishes.push(w);
        }
        return Array.from(byOwner.values()).sort((a, b) => {
            const aSelf = !a.isPet && a.userId === user?.id;
            const bSelf = !b.isPet && b.userId === user?.id;
            if (aSelf) return -1;
            if (bSelf) return 1;
            return (a.owner.name || '').localeCompare(b.owner.name || '');
        });
    }, [wishes, user]);

    // Which recipient the list is narrowed to (an `ownerKey`), or null for
    // everyone. The chip row is the primary control, but the Events tab can also
    // jump us straight to one person's wishes from a birthday — that arrives as
    // the `focusUserId` prop, which we translate into the same selection so both
    // entry points drive one piece of state.
    const [selectedKey, setSelectedKey] = useState(null);

    useEffect(() => {
        setSelectedKey(focusUserId ? `user:${focusUserId}` : null);
    }, [focusUserId]);

    const clearSelection = () => {
        setSelectedKey(null);
        // Also clear the parent's focus, or navigating back to this tab would
        // silently re-apply the birthday jump.
        if (focusUserId) onClearFocus();
    };

    const selectOwner = (key) => {
        if (key === null) {
            clearSelection();
            return;
        }
        setSelectedKey(key);
        if (focusUserId) onClearFocus();
    };

    // A selection can go stale when wishes reload (the person's last wish was
    // deleted, or they left the group). Fall back to showing everyone rather
    // than an empty list with no visible way out.
    const selectedSection = selectedKey
        ? ownerSections.find((s) => s.key === selectedKey)
        : null;
    const visibleSections = selectedSection ? [selectedSection] : ownerSections;

    const openCreate = () => {
        setModal({ editingId: null, initialForm: { ...BLANK_WISH, group_ids: [group.id] } });
    };

    const openEdit = async (wish) => {
        try {
            // Fetch full detail so we know which groups the wish is shared into.
            const res = await api.get(`/wishes/${wish.id}`);
            setModal({ editingId: wish.id, initialForm: wishToForm(res.data) });
        } catch (err) {
            notify('error', errText(err, 'Could not open that wish for editing.'));
        }
    };

    const handleDelete = async (wish) => {
        if (!window.confirm(`Delete "${wish.title}"? This can't be undone.`)) return;
        try {
            await api.delete(`/wishes/${wish.id}`);
            notify('success', 'Wish deleted.');
            onChanged();
        } catch (err) {
            notify('error', errText(err, 'Could not delete the wish.'));
        }
    };

    const handleArchive = async (wish) => {
        try {
            await api.post(`/wishes/${wish.id}/archive`);
            notify('success', 'Wish archived.');
            onChanged();
        } catch (err) {
            notify('error', errText(err, 'Could not archive the wish.'));
        }
    };

    const handleClaim = async (wish) => {
        try {
            await api.post(`/wishes/${wish.id}/claim`, { group_id: group.id });
            notify('success', 'You claimed this — others now see it as reserved.');
            onChanged();
        } catch (err) {
            notify('error', errText(err, 'Could not claim the wish.'));
        }
    };

    const handleUnclaim = async (wish) => {
        try {
            await api.delete(`/wishes/${wish.id}/claim`);
            notify('success', 'Claim released.');
            onChanged();
        } catch (err) {
            notify('error', errText(err, 'Could not release the claim.'));
        }
    };

    const openPurchase = (wish) => {
        setPurchaseTarget(wish);
        setPurchaseNote('');
        // Hint: default the reveal to the day AFTER the recipient's birthday, so
        // the surprise lasts through their special day. For a pet wish that's the
        // pet's birthday. Fully editable.
        const reveal = revealAfterBirthday(wishOwner(wish).birthday);
        setRevealDate(reveal ? reveal.iso : '');
    };

    const submitPurchase = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/wishes/${purchaseTarget.id}/purchase`, {
                group_id: group.id,
                private_note: purchaseNote || null,
                reveal_date: revealDate || null,
            });
            notify('success', 'Marked as purchased. The owner will not see this.');
            setPurchaseTarget(null);
            onChanged();
        } catch (err) {
            notify('error', errText(err, 'Could not record the purchase.'));
        }
    };

    const handleUndoPurchase = async (wish) => {
        try {
            await api.delete(`/wishes/${wish.id}/purchase`);
            notify('success', 'Purchase undone.');
            onChanged();
        } catch (err) {
            notify('error', errText(err, 'Could not undo the purchase.'));
        }
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Wishes in {group.name}</h2>
                <button
                    onClick={openCreate}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
                >
                    + Add Wish
                </button>
            </div>

            <PersonFilterBar
                wishes={wishes}
                value={selectedSection ? selectedKey : null}
                onChange={selectOwner}
                currentUserId={user?.id}
            />

            {wishes.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow text-gray-500">
                    No wishes here yet. Add one to get started.
                </div>
            ) : (
                <div className="space-y-8">
                    {visibleSections.map((section) => {
                        // Only for the section HEADING wording. The owner-vs-gifter
                        // branch on each card is derived per-wish inside WishGiftCard,
                        // never from this section-level guess.
                        const isSelfPerson = section.userId === user?.id && !section.isPet;
                        const bday = nextBirthday(section.owner.birthday);
                        return (
                            <div key={section.key}>
                                <div className="flex items-center gap-3 mb-3">
                                    {section.isPet && (
                                        <button
                                            onClick={() => navigate(`/pet/${section.owner.petId}`)}
                                            title="View pet profile"
                                            className="focus:outline-none"
                                        >
                                            <Avatar name={section.owner.name} url={section.owner.avatarUrl} color={section.owner.avatarColor} seed={section.owner.seed} size={32} />
                                        </button>
                                    )}
                                    <h3 className="text-md font-semibold text-gray-900">
                                        {isSelfPerson ? 'Your wishes' : section.owner.name || 'Unknown'}
                                    </h3>
                                    {section.isPet && <PetBadge species={section.owner.species} />}
                                    {bday && (
                                        <span className="text-xs px-2 py-0.5 rounded bg-pink-100 text-pink-700">
                                            🎂 {bday.label} · {countdownLabel(bday.daysUntil)}
                                        </span>
                                    )}
                                    <span className="text-xs text-gray-400">
                                        {section.wishes.length} item{section.wishes.length !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {section.wishes.map((wish) => {
                                        const owner = wishOwner(wish);
                                        return (
                                            <WishGiftCard
                                                key={wish.id}
                                                wish={wish}
                                                user={user}
                                                ownerActions={
                                                    <>
                                                        <button onClick={() => openEdit(wish)} className="text-sm text-gray-600 hover:text-gray-900">Edit</button>
                                                        <button onClick={() => handleArchive(wish)} className="text-sm text-gray-600 hover:text-gray-900">Archive</button>
                                                        <button onClick={() => handleDelete(wish)} className="text-sm text-red-600 hover:text-red-700">Delete</button>
                                                    </>
                                                }
                                                onClaim={() => handleClaim(wish)}
                                                onUnclaim={() => handleUnclaim(wish)}
                                                onPurchase={() => openPurchase(wish)}
                                                onUndoPurchase={() => handleUndoPurchase(wish)}
                                                notify={notify}
                                                onShareChanged={onChanged}
                                            >
                                                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                                                    For {owner.name}
                                                    {owner.isPet && <PetBadge species={owner.species} />}
                                                </p>
                                            </WishGiftCard>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create / edit wish modal (shared with My Wishes) */}
            {modal && (
                <WishFormModal
                    editingId={modal.editingId}
                    initialForm={modal.initialForm}
                    myGroups={myGroups}
                    categories={categories}
                    events={events}
                    lockedGroupId={group.id}
                    pets={pets}
                    childrenList={childrenList}
                    onClose={() => setModal(null)}
                    onSaved={() => {
                        setModal(null);
                        onChanged();
                    }}
                    notify={notify}
                />
            )}

            {/* Purchase modal */}
            {purchaseTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full">
                        <h3 className="text-lg font-semibold mb-1">Mark as purchased</h3>
                        <p className="text-sm text-gray-500 mb-4">
                            “{purchaseTarget.title}”. The owner will never see this.
                        </p>
                        <form onSubmit={submitPurchase} className="space-y-3">
                            <Field label="Reveal date (optional)">
                                <input type="date" value={revealDate} onChange={(e) => setRevealDate(e.target.value)} className={inputCls} />
                                {(() => {
                                    const owner = wishOwner(purchaseTarget);
                                    const reveal = revealAfterBirthday(owner.birthday);
                                    return (
                                        <p className="text-xs text-gray-400 mt-1">
                                            {reveal
                                                ? `Defaulted to the day after ${owner.name}'s birthday (${reveal.birthdayLabel}), so the surprise lasts through it. `
                                                : ''}
                                            On this date the owner may be shown who bought it.
                                        </p>
                                    );
                                })()}
                            </Field>
                            <Field label="Private note (optional)">
                                <textarea value={purchaseNote} onChange={(e) => setPurchaseNote(e.target.value)} className={inputCls} rows={2} placeholder="e.g., Ordered, arrives Dec 20" />
                            </Field>
                            <div className="flex gap-2 justify-end pt-2">
                                <button type="button" onClick={() => setPurchaseTarget(null)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md">
                                    Cancel
                                </button>
                                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                                    Confirm Purchase
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

const inputCls =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

